import type { ReactNode } from 'react';

import type { ChatItem, ToolUseItem } from '../../../contracts';
import type {
  BlockStatus,
  LatestTodoPlan,
  LiveDelegateStep,
  WorkingBlockDecision,
} from '../../../data';
import { StreamingChildRunPanel } from '../../agent-harness/components/child-run-inline-panel/streaming-child-run-panel';

/**
 * What this row reads from the timeline's renderer deps, and resets before the
 * child's own renderer gets them. Those deps are the dispatcher's, so the row is
 * generic over them and hands the same shape back to the dispatcher's factory.
 */
interface ChildRunRendererDeps {
  agentRunId: string;
  allowChildStreams: boolean;
  items?: ChatItem[];
  latestPersistentChildToolCallIds?: ReadonlySet<string>;
  liveDelegateStep?: LiveDelegateStep;
  latestTodoPlan?: LatestTodoPlan | null;
  workingBlocks?: Map<string, WorkingBlockDecision>;
  turnInFlight?: boolean;
  inlineReplyActions?: ReadonlyMap<string, (copyableText: string | null) => ReactNode>;
}

export const renderChildRunToolUse = <Deps extends ChildRunRendererDeps>(
  item: ToolUseItem,
  deps: Deps,
  status: BlockStatus,
  createProjectItemRenderer: (deps: Deps) => (item: ChatItem) => ReactNode,
  /** Cancels the child run's turn; rejects when the cancel fails. */
  cancelChildRun: (childAgentRunId: string) => Promise<void>
): ReactNode | null => {
  const awaitingChildRun =
    item.name === 'invoke_agent' && item.status === 'running' && !item.childAgentRunId;
  if (!item.childAgentRunId && !awaitingChildRun) return null;

  const childStatus =
    status === 'error'
      ? 'error'
      : status === 'cancelled'
        ? 'cancelled'
        : status === 'success'
          ? 'done'
          : 'running';

  // The CUA stop button never opens for a headless child, so this row
  // carries Stop itself — scoped to the file_tools delegate while running
  // (legacy invoke_agent rows unchanged): the cancel targets the CHILD run,
  // leaving the parent turn alive. Keyed on the tool name because the fold
  // does not carry the child `kind` metadata onto the item.
  const childRunId = item.childAgentRunId;
  const onStop =
    item.name === 'run_file_task' && childRunId && childStatus === 'running'
      ? () => cancelChildRun(childRunId)
      : undefined;

  return (
    <StreamingChildRunPanel
      childAgentRunId={item.childAgentRunId}
      childAgentName={item.childAgentName}
      status={childStatus}
      onStop={onStop}
      renderItem={
        item.childAgentRunId
          ? createProjectItemRenderer({
              ...deps,
              agentRunId: item.childAgentRunId,
              allowChildStreams: false,
              items: undefined,
              latestPersistentChildToolCallIds: undefined,
              liveDelegateStep: undefined,
              // The parent's plan must not suppress a child run's todo rows —
              // the anchor lives in the parent timeline, not this panel.
              latestTodoPlan: null,
              // Keyed by the parent timeline's ids: a colliding child id would
              // vanish as a `member`, or draw the parent's block in this panel.
              workingBlocks: undefined,
              turnInFlight: false,
              inlineReplyActions: undefined,
            })
          : undefined
      }
      shouldMountLifecycle={shouldMountChildLifecycle(item, deps)}
    />
  );
};

const shouldMountChildLifecycle = (
  item: ToolUseItem,
  deps: ChildRunRendererDeps
): boolean => {
  if (!deps.allowChildStreams || !item.childAgentRunId) return false;
  if (!item.childPersistent) return true;
  return deps.latestPersistentChildToolCallIds?.has(item.id) ?? false;
};
